import {
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bold,
  Check,
  ChevronRight,
  ClipboardList,
  FileText,
  Forklift,
  Italic,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  Type,
  Underline,
  UserPlus,
  X,
} from 'lucide-react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import {
  calculateOrderPosition,
  calculateOrderTotals,
  germanVatRates,
  INTERNAL_CUSTOMER_NUMBER,
  resolveOrderGridNavigation,
  type NewCustomerDraft,
  type OrderAddressInput,
  type OrderConditions,
  type OrderCustomerOption,
  type OrderGateway,
  type OrderItemOption,
  type OrderListBucket,
  type OrderMachineOption,
  type OrderOverview,
  type OrderPage,
  type OrderPositionInput,
  type OrderPositionKind,
  type OrderProfile,
  type OrderRecord,
  type OrderStatus,
} from '@forq/orders-data';
import {
  type ContextMenuItem,
  DataTable,
  type DataTableColumn,
  CustomizableMetrics,
  describeDeleteOutcome,
  EmptyState,
  FilterBar,
  FlowTrail,
  MetricTile,
  RecordDeleteButton,
  RecordField,
  RecordPanel,
  SearchSelect,
  StatusPill,
  type StatusTone,
  Tabs,
  type TabItem,
  useContextMenu,
  useRecordDeleteFlow,
  WorkspacePage,
} from '@forq/ui';
import type { MasterEntry, MastersGateway } from '@forq/masters-data';
import { buildOrderFlow, orderFlowTransitions, statusLabels } from './orderFlow';

interface OrdersPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: OrderGateway;
  readonly deletionGateway: RecordDeletionGateway;
  readonly onSelectedOrderChange?: (order: OrderRecord | null) => void;
  /** Legt inline einen neuen Kunden an und liefert die fertige Option zum sofortigen Übernehmen. */
  readonly onCreateCustomer?: (draft: NewCustomerDraft) => Promise<OrderCustomerOption>;
  /** Für den Kostenstellen-Picker im Kopfdaten-Schritt (Etappe 1b). */
  readonly mastersGateway?: MastersGateway;
  /** Zusatzinhalt für das Vorgangs-Fenster (Beleg-Aktions-Center + Abrechnung), von der Workspace injiziert. */
  readonly detailChildren?: ReactNode;
  /** Erhöht sich extern (z. B. nach dem Abrechnen im Fenster) und lädt die Liste neu -- so aktualisiert
   *  sich der Vorgangs-Status im Fenster sofort, ohne erneutes Öffnen. */
  readonly reloadKey?: number;
}

interface PositionDraft extends OrderPositionInput {
  readonly rowId: string;
}

interface WorkbenchDraft {
  readonly orderId?: string;
  readonly step: 1 | 2 | 3 | 4 | 5;
  readonly profile: OrderProfile;
  readonly title: string;
  readonly customerId: string | null;
  readonly contactName: string;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly machineId: string | null;
  readonly priority: 'low' | 'normal' | 'high' | 'urgent';
  readonly assignedTo: string;
  readonly dueAt: string | null;
  readonly notes: string;
  readonly addresses: readonly OrderAddressInput[];
  /** Solange true, spiegelt die Lieferadresse die Rechnungsadresse. Sobald die Lieferadresse einzeln
   *  geändert wird, entkoppelt sie sich (dann ändert eine Rechnungsadressänderung sie nicht mehr mit). */
  readonly deliveryFollowsBilling: boolean;
  readonly conditions: Partial<OrderConditions>;
  readonly positions: readonly PositionDraft[];
}

const emptyPage: OrderPage = { items: [], total: 0, page: 1, pageSize: 25 };
const emptyOverview: OrderOverview = { newCount: 0, inProgressCount: 0, completedCount: 0, billingReadyCount: 0 };
// Reihenfolge = Anzeige-Reihenfolge im Profil-Schritt (der Assistent iteriert über die Keys).
const profileLabels: Record<OrderProfile, string> = {
  general: 'Normaler Vorgang',
  service: 'Serviceauftrag',
  sales: 'Maschinenverkauf',
  internal: 'Interner Vorgang',
  rental: 'Mietauftrag',
  'spare-parts': 'Ersatzteilverkauf',
  accessories: 'Zubehörverkauf',
  'trade-in': 'Inzahlungnahme',
};
// Die im Vorgang neu anlegbaren Arten (Spec 2026-07-29). Miete läuft über den Mietpark und erzeugt
// erst beim Abschluss einen Vorgang; Ersatzteil/Zubehör/Inzahlungnahme sind Alt-Profile und gehen im
// „Normalen Vorgang" auf. Bestandsvorgänge mit anderen Profilen bleiben über profileLabels sichtbar.
const creatableProfiles: readonly OrderProfile[] = ['general', 'service', 'sales', 'internal'];
function emptyNewCustomer(): NewCustomerDraft {
  return { kind: 'company', companyName: '', firstName: '', lastName: '', street: '', postalCode: '', city: '', phone: '', email: '' };
}
const positionKindLabels: Record<OrderPositionKind, string> = {
  machine: 'Maschine', article: 'Artikel', service: 'Dienstleistung', labor: 'Arbeitszeit',
  travel: 'Fahrtkosten', rental: 'Miete', transport: 'Transport', text: 'Text',
};
const bucketTabs: readonly { readonly id: OrderListBucket; readonly label: string }[] = [
  { id: 'new', label: 'Offen' },
  { id: 'in-progress', label: 'In Arbeit' },
  { id: 'completed', label: 'Abgeschlossen' },
  { id: 'all', label: 'Alle' },
];
/** Gängige Lieferbedingungen (Incoterms 2020 + geläufige deutsche Begriffe) als Vorschläge im
 *  Auswahlfeld. Über die datalist wählbar ODER frei eingebbar (eigene Bedingungen bleiben möglich). */
const deliveryTermOptions: readonly string[] = [
  'EXW – Ab Werk Werl',
  'FCA – Frei Frachtführer',
  'CPT – Frachtfrei',
  'CIP – Frachtfrei versichert',
  'DAP – Geliefert benannter Ort (frei Haus)',
  'DPU – Geliefert entladen',
  'DDP – Geliefert verzollt',
  'frei Haus',
  'ab Werk',
  'Selbstabholung',
  'unfrei',
];

/**
 * Lieferbedingungen als echtes Dropdown (wie Belegsprache/Kostenstelle). Wählt man „Sonstiges …",
 * erscheint darunter ein Freitextfeld für eine eigene Bedingung. Gespeichert wird weiterhin ein
 * einzelner String (bekannte Option ODER der freie Text).
 */
function DeliveryTermsField({ value, onChange }: { readonly value: string; readonly onChange: (next: string) => void }) {
  const isKnown = deliveryTermOptions.includes(value);
  const [customMode, setCustomMode] = useState(!isKnown && value !== '');
  const showCustom = customMode || (!isKnown && value !== '');
  return (
    <>
      <select
        value={showCustom ? '__custom__' : value}
        onChange={(event) => {
          if (event.target.value === '__custom__') { setCustomMode(true); onChange(''); }
          else { setCustomMode(false); onChange(event.target.value); }
        }}
      >
        <option value="">— keine —</option>
        {deliveryTermOptions.map((term) => <option key={term} value={term}>{term}</option>)}
        <option value="__custom__">Sonstiges (eigene eingeben) …</option>
      </select>
      {showCustom ? (
        <input value={value} onChange={(event) => onChange(event.target.value)} placeholder="Eigene Lieferbedingung eingeben" />
      ) : null}
    </>
  );
}

const gridColumns = ['kind', 'sourceNumber', 'description', 'quantity', 'unit', 'unitPrice', 'discountPercent', 'vatRate'] as const;

type GridColumn = typeof gridColumns[number];

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

function dateLabel(value: string | null): string {
  if (!value) return '–';
  return new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`));
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Vorgang konnte nicht verarbeitet werden.';
}

function emptyPosition(kind: OrderPositionKind = 'service'): PositionDraft {
  return {
    rowId: crypto.randomUUID(),
    kind,
    sourceId: null,
    sourceNumber: '',
    machineId: null,
    description: '',
    quantity: 1,
    unit: 'Stück',
    unitPrice: 0,
    purchasePrice: 0,
    discountPercent: 0,
    vatRate: 19,
    internalText: '',
    customerText: '',
  };
}

function emptyAddress(kind: 'billing' | 'delivery' | 'site', label: string): OrderAddressInput {
  return { kind, label, companyName: '', contactName: '', street: '', postalCode: '', city: '', countryCode: 'DE' };
}

function newDraft(): WorkbenchDraft {
  return {
    step: 1,
    profile: 'general',
    title: '',
    customerId: null,
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    machineId: null,
    priority: 'normal',
    assignedTo: '',
    dueAt: null,
    notes: '',
    addresses: [emptyAddress('billing', 'Rechnungsadresse'), emptyAddress('delivery', 'Lieferadresse')],
    deliveryFollowsBilling: true,
    conditions: {
      customerReference: '',
      paymentTermDays: 14,
      cashDiscountPercent: 0,
      cashDiscountDays: 0,
      orderDiscountPercent: 0,
      requestedDate: null,
      deliveryMethod: '',
      deliveryTerms: '',
    },
    positions: [emptyPosition()],
  };
}

function draftFromOrder(order: OrderRecord): WorkbenchDraft {
  return {
    orderId: order.id,
    step: 5,
    profile: order.profile,
    title: order.title,
    customerId: order.customerId,
    contactName: order.contactName,
    contactEmail: order.contactEmail,
    contactPhone: order.contactPhone,
    machineId: order.machineId,
    priority: order.priority,
    assignedTo: order.assignedTo,
    dueAt: order.dueAt,
    notes: order.notes,
    addresses: order.addresses.map(
      (address): OrderAddressInput => ({
        kind: address.kind,
        label: address.label,
        companyName: address.companyName,
        contactName: address.contactName,
        street: address.street,
        postalCode: address.postalCode,
        city: address.city,
        countryCode: address.countryCode,
      }),
    ),
    deliveryFollowsBilling: false,
    conditions: order.conditions,
    positions: order.positions.map((position) => ({
      rowId: position.id,
      kind: position.kind,
      sourceId: position.sourceId,
      sourceNumber: position.sourceNumber,
      machineId: position.machineId,
      description: position.description,
      quantity: position.quantity,
      unit: position.unit,
      unitPrice: position.unitPrice,
      purchasePrice: position.purchasePrice,
      discountPercent: position.discountPercent,
      vatRate: position.vatRate,
      internalText: position.internalText,
      customerText: position.customerText,
    })),
  };
}

function focusCell(rowIndex: number, columnIndex: number): void {
  window.setTimeout(() => {
    document.querySelector<HTMLElement>(`[data-order-cell="${rowIndex}-${columnIndex}"]`)?.focus();
  }, 0);
}

const bucketTone: Record<Exclude<OrderListBucket, 'all'>, StatusTone> = {
  new: 'new',
  'in-progress': 'progress',
  completed: 'done',
};

function statusTone(order: OrderRecord): StatusTone {
  if (order.status === 'cancelled') return 'danger';
  return bucketTone[order.bucket];
}

const orderColumns: readonly DataTableColumn<OrderRecord>[] = [
  {
    key: 'number',
    header: 'Vorgang',
    width: 'minmax(0, 1.4fr)',
    render: (order) => (
      <span className="forq-stack"><strong>{order.number}</strong><small>{order.title}</small></span>
    ),
  },
  {
    key: 'customer',
    header: 'Kunde / Maschine',
    width: 'minmax(0, 1.6fr)',
    render: (order) => (
      <span className="forq-stack">
        <strong>{order.customerName}</strong>
        <small>{order.machineLabel || 'ohne Bezugsmaschine'}</small>
      </span>
    ),
  },
  { key: 'profile', header: 'Art', render: (order) => profileLabels[order.profile] },
  {
    key: 'status',
    header: 'Status',
    render: (order) => <StatusPill label={statusLabels[order.status]} tone={statusTone(order)} />,
  },
  { key: 'due', header: 'Fällig', width: '110px', render: (order) => dateLabel(order.dueAt) },
  { key: 'assignee', header: 'Bearbeiter', render: (order) => order.assignedTo || 'nicht zugewiesen' },
  {
    key: 'total',
    header: 'Wert',
    width: '120px',
    align: 'end',
    render: (order) => <span className="forq-cell-num">{money(order.grossTotal)}</span>,
  },
];

export function OrdersPage({ tenant, mode, permissions, enabledFeatureIds, gateway, deletionGateway, onSelectedOrderChange, onCreateCustomer, mastersGateway, detailChildren, reloadKey }: OrdersPageProps) {
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const [overview, setOverview] = useState<OrderOverview>(emptyOverview);
  const [page, setPage] = useState<OrderPage>(emptyPage);
  // Deep links (from FORQ Command/Signal or a reload) may target a Vorgang in any bucket, so
  // start on "Alle" when a record is addressed — otherwise the default "Neu" filter would hide it.
  const [bucket, setBucket] = useState<OrderListBucket>(orderNumber ? 'all' : 'new');
  const [profile, setProfile] = useState<OrderProfile | 'all'>('all');
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState<WorkbenchDraft | null>(null);
  const [customers, setCustomers] = useState<readonly OrderCustomerOption[]>([]);
  const [newCustomer, setNewCustomer] = useState<NewCustomerDraft | null>(null);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [costCenters, setCostCenters] = useState<readonly MasterEntry[]>([]);
  useEffect(() => {
    if (!mastersGateway) return undefined;
    let active = true;
    mastersGateway.listCostCenters(tenant.id).then((entries) => { if (active) setCostCenters(entries); }).catch(() => { if (active) setCostCenters([]); });
    return () => { active = false; };
  }, [mastersGateway, tenant.id]);
  const [machines, setMachines] = useState<readonly OrderMachineOption[]>([]);
  const [includeAllMachines, setIncludeAllMachines] = useState(false);
  const [machineQuery, setMachineQuery] = useState('');
  const [items, setItems] = useState<readonly OrderItemOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The opened record is derived from the URL (/vorgaenge/:orderNumber) — a single source of
  // truth that is deep-linkable, survives reload and drives the workspace document panel.
  const selected = orderNumber ? page.items.find((item) => item.number === orderNumber) ?? null : null;

  const canCreate = permissions.has('orders.create') && enabledFeatureIds.includes('orders.create');
  const canEdit = permissions.has('orders.edit') && enabledFeatureIds.includes('orders.workbench');
  const flowTransitions = selected ? orderFlowTransitions(selected, permissions) : [];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextOverview, nextPage] = await Promise.all([
        gateway.getOverview(tenant.id),
        gateway.searchOrders({ tenantId: tenant.id, bucket, profile, query, pageSize: 50 }),
      ]);
      setOverview(nextOverview);
      setPage(nextPage);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [bucket, gateway, profile, query, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  // Nach dem Abrechnen im Fenster (reloadKey++) den offenen Vorgang frisch nachladen -- getOrder umgeht
  // den Listen-Cache des StableOrdersGateway -- und in der Liste ersetzen, damit der Status im Fenster
  // sofort auf „Abgerechnet" springt (der Billing-Panel läuft über ein anderes Gateway und würde den
  // Cache sonst nicht invalidieren).
  const selectedId = selected?.id ?? null;
  useEffect(() => {
    if (!reloadKey || !selectedId) return undefined;
    let active = true;
    void gateway.getOrder(tenant.id, selectedId).then((fresh) => {
      if (active) setPage((current) => ({ ...current, items: current.items.map((item) => item.id === fresh.id ? fresh : item) }));
    }).catch(() => {});
    return () => { active = false; };
  }, [reloadKey, selectedId, gateway, tenant.id]);
  useEffect(() => { setEditor(null); }, [tenant.id]);

  // FORQ-eigenes Rechtsklick-Menü auf Vorgangs-Zeilen.
  const canDeleteOrder = permissions.has('orders.delete');
  const { open: openOrderMenu, menu: orderContextMenu } = useContextMenu();
  const { requestDelete: requestOrderDelete, deleteDialog: orderDeleteDialog } = useRecordDeleteFlow(deletionGateway, tenant.id);
  const orderMenuItems = useCallback((order: OrderRecord): ContextMenuItem[] => [
    { label: 'Öffnen', icon: <FileText size={15} />, onSelect: () => navigate(`/vorgaenge/${order.number}`) },
    {
      label: 'Löschen', icon: <Trash2 size={15} />, danger: true, disabled: !canDeleteOrder, separatorBefore: true,
      onSelect: () => requestOrderDelete({
        entity: 'order', id: order.id, noun: 'Vorgang', label: order.number,
        onDone: (outcome) => { setNotice(describeDeleteOutcome(outcome, 'Vorgang', order.number)); void load(); },
      }),
    },
  ], [navigate, canDeleteOrder, requestOrderDelete, load]);

  useEffect(() => { onSelectedOrderChange?.(selected); }, [onSelectedOrderChange, selected]);

  // Vorgangs-Fenster mit Escape schließen -- aber nur, wenn kein Editor offen ist (der hat Vorrang).
  useEffect(() => {
    if (!selected || editor) return undefined;
    const onKey = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape') navigate('/vorgaenge'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, editor, navigate]);

  // Maschinenpicker auf die Kundenmaschinen fokussieren: bei gewähltem Kunden nur dessen Maschinen
  // (live nachgeladen, damit auch eine gerade neu angelegte erscheint); "andere Maschine" zeigt alle.
  // Die Suche läuft serverseitig (machineQuery): so wird jede Maschine gefunden, auch bei großem
  // Bestand jenseits der Ergebnis-Kappung – nicht nur die zufällig vorgeladenen.
  const editorCustomerId = editor?.customerId ?? null;
  // FTW-intern (Kunde 69999): auch den eigenen Verkaufs-/Mietbestand zeigen — interne Vorgänge laufen
  // auf den eigenen Bestand, nicht nur auf kundeneigene Maschinen (Spec 2026-07-29).
  const isInternalCustomer = editorCustomerId !== null && customers.some((entry) => entry.id === editorCustomerId && entry.number === INTERNAL_CUSTOMER_NUMBER);
  const showAllMachines = includeAllMachines || isInternalCustomer;
  useEffect(() => {
    if (!editor) return undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      gateway.searchMachineOptions(tenant.id, machineQuery, showAllMachines ? null : editorCustomerId, showAllMachines)
        .then((options) => { if (active) setMachines(options); })
        .catch(() => { /* Ladefehler werden beim Öffnen/Speichern gemeldet */ });
    }, machineQuery.trim() ? 180 : 0);
    return () => { active = false; window.clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorCustomerId, showAllMachines, machineQuery, gateway, tenant.id, Boolean(editor)]);

  const openCreate = useCallback(async () => {
    setError(null);
    try {
      const [customerOptions, machineOptions, itemOptions] = await Promise.all([
        gateway.searchCustomerOptions(tenant.id),
        gateway.searchMachineOptions(tenant.id),
        gateway.searchItemOptions(tenant.id),
      ]);
      setCustomers(customerOptions);
      setMachines(machineOptions);
      setItems(itemOptions);
      setIncludeAllMachines(false);
      setMachineQuery('');
      setEditor(newDraft());
    } catch (openError) {
      setError(errorMessage(openError));
    }
  }, [gateway, tenant.id]);

  // Vom Startbild aus (?neu) direkt den Vorgangs-Assistenten öffnen -- ein Klick weniger.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('neu') === null || !canCreate) return;
    void openCreate();
    setSearchParams((prev) => { prev.delete('neu'); return prev; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = useCallback(async (order: OrderRecord) => {
    setError(null);
    try {
      const [detail, customerOptions, machineOptions, itemOptions] = await Promise.all([
        gateway.getOrder(tenant.id, order.id),
        gateway.searchCustomerOptions(tenant.id),
        gateway.searchMachineOptions(tenant.id),
        gateway.searchItemOptions(tenant.id),
      ]);
      setCustomers(customerOptions);
      setMachines(machineOptions);
      setItems(itemOptions);
      setIncludeAllMachines(false);
      setMachineQuery('');
      setEditor(draftFromOrder(detail));
    } catch (openError) {
      setError(errorMessage(openError));
    }
  }, [gateway, tenant.id]);

  const updateEditor = useCallback(<K extends keyof WorkbenchDraft>(key: K, value: WorkbenchDraft[K]) => {
    setEditor((current) => current ? { ...current, [key]: value } : current);
  }, []);

  const applyCustomerToEditor = useCallback((customer: OrderCustomerOption | null) => {
    setEditor((current) => current ? {
      ...current,
      customerId: customer?.id ?? null,
      contactName: customer?.contactName ?? '',
      contactEmail: customer?.email ?? '',
      contactPhone: customer?.phone ?? '',
      addresses: customer
        ? [customer.billingAddress, customer.deliveryAddress ?? { ...customer.billingAddress, kind: 'delivery', label: 'Lieferadresse' }]
        : current.addresses,
      // Hat der Kunde eine eigene Lieferadresse, ist sie entkoppelt; sonst spiegelt sie die Rechnungsadresse.
      deliveryFollowsBilling: customer ? !customer.deliveryAddress : current.deliveryFollowsBilling,
    } : current);
  }, []);

  const selectCustomer = useCallback((customerId: string) => {
    applyCustomerToEditor(customers.find((item) => item.id === customerId) ?? null);
  }, [customers, applyCustomerToEditor]);

  // Inline neuen Kunden anlegen: über den Callback (der den customersGateway hält) erzeugen, die fertige
  // Option lokal einreihen und direkt übernehmen. selectCustomer würde die noch nicht im State stehende
  // Option nicht finden -> deshalb applyCustomerToEditor mit der frischen Option.
  const submitNewCustomer = useCallback(async () => {
    if (!onCreateCustomer || !newCustomer) return;
    setSavingCustomer(true);
    setCustomerError(null);
    try {
      const option = await onCreateCustomer(newCustomer);
      setCustomers((current) => [option, ...current.filter((entry) => entry.id !== option.id)]);
      applyCustomerToEditor(option);
      setNewCustomer(null);
    } catch (createError) {
      setCustomerError(createError instanceof Error && createError.message ? createError.message : 'Der Kunde konnte nicht angelegt werden.');
    } finally {
      setSavingCustomer(false);
    }
  }, [onCreateCustomer, newCustomer, applyCustomerToEditor]);

  // Interne Aufträge laufen immer auf den eigenen Betrieb -> beim Umschalten auf „Interner Vorgang"
  // den festen internen Kunden (69999) automatisch vorwählen; alle anderen Profile lassen die Wahl offen.
  const selectProfile = useCallback((profile: OrderProfile) => {
    updateEditor('profile', profile);
    if (profile === 'internal') {
      const internal = customers.find((item) => item.number === INTERNAL_CUSTOMER_NUMBER);
      if (internal) selectCustomer(internal.id);
    }
  }, [updateEditor, customers, selectCustomer]);

  const updateAddress = useCallback((index: number, key: keyof OrderAddressInput, value: string) => {
    setEditor((current) => {
      if (!current) return current;
      const target = current.addresses[index];
      if (!target) return current;
      const editingBilling = target.kind === 'billing';
      const editingDelivery = target.kind === 'delivery';
      const addresses = current.addresses.map((address, addressIndex) => {
        if (addressIndex === index) return { ...address, [key]: value };
        // Rechnungsadresse ändern spiegelt in die Lieferadresse, solange sie gekoppelt ist.
        if (editingBilling && current.deliveryFollowsBilling && address.kind === 'delivery') return { ...address, [key]: value };
        return address;
      });
      // Wird die Lieferadresse einzeln geändert, entkoppelt sie sich von der Rechnungsadresse.
      return { ...current, addresses, deliveryFollowsBilling: editingDelivery ? false : current.deliveryFollowsBilling };
    });
  }, []);

  const updateCondition = useCallback((key: keyof OrderConditions, value: string | number | null) => {
    setEditor((current) => current ? { ...current, conditions: { ...current.conditions, [key]: value } } : current);
  }, []);

  const updatePosition = useCallback((rowIndex: number, key: GridColumn, rawValue: string) => {
    setEditor((current) => {
      if (!current) return current;
      const positions = current.positions.map((position, index) => {
        if (index !== rowIndex) return position;
        const numberKeys: readonly GridColumn[] = ['quantity', 'unitPrice', 'discountPercent', 'vatRate'];
        const value = numberKeys.includes(key) ? Number(rawValue.replace(',', '.')) || 0 : rawValue;
        return { ...position, [key]: value };
      });
      return { ...current, positions };
    });
  }, []);

  const applyItemOption = useCallback((rowIndex: number, option: OrderItemOption) => {
    setEditor((current) => current ? {
      ...current,
      positions: current.positions.map((position, index) => index === rowIndex ? {
        ...position,
        kind: option.kind,
        sourceId: option.id,
        sourceNumber: option.number,
        machineId: null,
        description: option.name,
        unit: option.unit,
        unitPrice: option.salesPrice,
        purchasePrice: option.purchasePrice,
        vatRate: option.vatRate,
      } : position),
    } : current);
  }, []);

  const applyMachineOption = useCallback((rowIndex: number, machine: OrderMachineOption) => {
    setEditor((current) => current ? {
      ...current,
      positions: current.positions.map((position, index) => index === rowIndex ? {
        ...position,
        kind: 'machine',
        sourceId: machine.id,
        sourceNumber: machine.number,
        machineId: machine.id,
        description: `${machine.manufacturer} ${machine.model}${machine.serialNumber ? ` · SN ${machine.serialNumber}` : ''}`.trim(),
        unit: 'Stück',
      } : position),
    } : current);
  }, []);

  // Auf Zahleneingabe im Nummernfeld: passenden Katalogartikel aus der geladenen Liste übernehmen.
  const applyCatalogItem = useCallback((rowIndex: number, sourceNumber: string) => {
    const option = items.find((item) => item.number.toLowerCase() === sourceNumber.trim().toLowerCase());
    if (option) applyItemOption(rowIndex, option);
  }, [items, applyItemOption]);

  // Such-Fenster für eine Position: Artikel/Dienstleistung oder -- bei Positionsart „Maschine" -- Maschine.
  const [positionSearch, setPositionSearch] = useState<{ readonly rowIndex: number; readonly mode: 'item' | 'machine' } | null>(null);
  // Positionstext-Editor (Rich-Text) für eine Position -- der ausführliche, kundenseitige Positionstext.
  const [textEditor, setTextEditor] = useState<number | null>(null);
  const applyPositionText = useCallback((rowIndex: number, html: string) => {
    setEditor((current) => current ? {
      ...current,
      positions: current.positions.map((position, index) => index === rowIndex ? { ...position, customerText: html } : position),
    } : current);
  }, []);

  const handleGridKey = useCallback((event: KeyboardEvent<HTMLElement>, rowIndex: number, columnIndex: number) => {
    if (event.key !== 'Enter' && event.key !== 'Tab') return;
    const rowCount = editor?.positions.length ?? 0;
    const action = resolveOrderGridNavigation({
      key: event.key,
      shiftKey: event.shiftKey,
      rowIndex,
      rowCount,
      columnIndex,
      columnCount: gridColumns.length,
    });
    event.preventDefault();
    if (action.kind === 'append-row') {
      setEditor((current) => current ? { ...current, positions: [...current.positions, emptyPosition()] } : current);
      focusCell(rowIndex + 1, 0);
      return;
    }
    if (action.kind === 'previous-cell') {
      const flatIndex = rowIndex * gridColumns.length + columnIndex - 1;
      if (flatIndex >= 0) focusCell(Math.floor(flatIndex / gridColumns.length), flatIndex % gridColumns.length);
      return;
    }
    const flatIndex = rowIndex * gridColumns.length + columnIndex + 1;
    focusCell(Math.floor(flatIndex / gridColumns.length), flatIndex % gridColumns.length);
  }, [editor?.positions.length]);

  const saveEditor = useCallback(async () => {
    if (!editor) return;
    // Kostenstelle ist Pflicht (sofern Kostenstellen gepflegt sind) -- sonst zurück zu den Kopfdaten.
    if (costCenters.length > 0 && !editor.conditions.costCenterId) {
      setError('Bitte im Schritt „Kopfdaten" eine Kostenstelle wählen (Pflichtfeld).');
      setEditor((current) => current ? { ...current, step: 4 } : current);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await gateway.saveOrder({
        tenantId: tenant.id,
        orderId: editor.orderId,
        profile: editor.profile,
        title: editor.title || profileLabels[editor.profile],
        customerId: editor.customerId,
        contactName: editor.contactName,
        contactEmail: editor.contactEmail,
        contactPhone: editor.contactPhone,
        machineId: editor.machineId,
        priority: editor.priority,
        assignedTo: editor.assignedTo,
        dueAt: editor.dueAt,
        notes: editor.notes,
        addresses: editor.addresses,
        conditions: editor.conditions,
        positions: editor.positions,
      });
      setEditor(null);
      setBucket(saved.bucket);
      navigate(`/vorgaenge/${saved.number}`);
      await load();
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setSaving(false);
    }
  }, [editor, gateway, load, tenant.id, costCenters]);

  // Advance a Vorgang along its status trail (FORQ Flow). The record is patched in place so it
  // stays visible even when the new status moves it to a different bucket; metrics are refreshed.
  const advanceStatus = useCallback(async (orderId: string, toStatus: OrderStatus) => {
    setError(null);
    try {
      const updated = await gateway.transitionOrder({ tenantId: tenant.id, orderId, toStatus });
      setPage((current) => ({ ...current, items: current.items.map((item) => item.id === updated.id ? updated : item) }));
      setOverview(await gateway.getOverview(tenant.id));
    } catch (transitionError) {
      setError(errorMessage(transitionError));
    }
  }, [gateway, tenant.id]);

  useEffect(() => {
    if (!editor || editor.step !== 5) return undefined;
    const listener = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        void saveEditor();
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [editor, saveEditor]);

  const calculated = useMemo(() => {
    if (!editor) return null;
    try {
      const positions = editor.positions
        .filter((position) => position.description.trim())
        .map((position, index) => calculateOrderPosition(position, index + 1, position.rowId));
      return calculateOrderTotals(positions, Number(editor.conditions.orderDiscountPercent ?? 0));
    } catch {
      return null;
    }
  }, [editor]);

  if (!enabledFeatureIds.includes('orders.list')) {
    return <EmptyState eyebrow="Vorgänge" title="Die Vorgangsliste ist für diese Rolle deaktiviert." description="Die Funktion kann in der Administration freigeschaltet werden." />;
  }

  const bucketTabItems: readonly TabItem[] = bucketTabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
    badge:
      tab.id === 'new' ? overview.newCount
      : tab.id === 'in-progress' ? overview.inProgressCount
      : tab.id === 'completed' ? overview.completedCount
      : undefined,
  }));

  return (
    <WorkspacePage
      customizeKey="orders-overview"
      eyebrow={`FORQ Orders · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Vorgänge"
      description="Neue, laufende und abgeschlossene Geschäftsfälle ohne vorgeschaltete Prozessgrafik."
      actions={canCreate ? (
        <button type="button" className="orders-page__create" onClick={() => void openCreate()}>
          <Plus size={18} /> Vorgang erstellen
        </button>
      ) : undefined}
      metrics={(
        <CustomizableMetrics
          pageKey="orders-overview"
          tiles={[
            { key: 'new', node: <MetricTile label="Offen" value={overview.newCount} hint="noch nicht begonnen" /> },
            { key: 'inprogress', node: <MetricTile label="In Arbeit" value={overview.inProgressCount} hint="läuft, noch nicht abgeschlossen" /> },
            { key: 'completed', node: <MetricTile label="Abgeschlossen" value={overview.completedCount} hint="inkl. stornierter Vorgänge" /> },
          ]}
        />
      )}
      toolbar={(
        <>
          <Tabs items={bucketTabItems} activeId={bucket} onSelect={(id) => setBucket(id as OrderListBucket)} ariaLabel="Vorgangsfilter" />
          <FilterBar
            search={{ value: query, onChange: setQuery, placeholder: 'Nummer, Kunde, Maschine oder Bearbeiter …' }}
            meta={`${page.total} Treffer`}
          >
            <SearchSelect value={profile} onChange={(next) => setProfile(next as OrderProfile | 'all')} ariaLabel="Vorgangsart" options={[
              { value: 'all', label: 'Alle Vorgangsarten' },
              ...Object.entries(profileLabels).map(([value, label]) => ({ value, label })),
            ]} />
          </FilterBar>
        </>
      )}
    >
      {error && !editor && <div className="orders-page__error">{error}</div>}
      {notice && <div className="orders-page__notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice(null)}>Schließen</button></div>}
      <DataTable
        columns={orderColumns}
        rows={page.items}
        rowKey={(order) => order.id}
        onRowSelect={(order) => navigate(`/vorgaenge/${order.number}`)}
        onRowContext={(event, order) => openOrderMenu(event, orderMenuItems(order))}
        isRowSelected={(order) => selected?.id === order.id}
        loading={loading}
        loadingLabel="Vorgänge werden geladen …"
        emptyLabel="In diesem Reiter sind keine Vorgänge vorhanden."
        ariaLabel="Vorgangsliste"
      />

      {selected && !editor && (
        <div className="orders-detail-modal" role="dialog" aria-modal="true" aria-label={`Vorgang ${selected.number}`} onMouseDown={(event) => { if (event.target === event.currentTarget) navigate('/vorgaenge'); }}>
          <div className="orders-detail-modal__panel">
            <button type="button" className="orders-detail-modal__close" onClick={() => navigate('/vorgaenge')} aria-label="Fenster schließen"><X size={18} /></button>
            <div className="orders-detail-modal__scroll">
              <RecordPanel
                eyebrow="Ausgewählter Vorgang"
                title={`${selected.number} · ${selected.title}`}
                subtitle={`${selected.customerName}${selected.machineLabel ? ` · ${selected.machineLabel}` : ''}`}
                status={<StatusPill label={statusLabels[selected.status]} tone={statusTone(selected)} />}
                actions={<>
                  {canEdit && !['completed', 'cancelled'].includes(selected.status) ? (
                    <button type="button" onClick={() => void openEdit(selected)}><Pencil size={15} /> In Workbench öffnen</button>
                  ) : null}
                  {/* Der Knopf erscheint auch bei abgerechneten Vorgängen -- der Dialog erklärt dann,
                      warum es nicht geht (Aufbewahrungspflicht), statt den Knopf wortlos zu verstecken. */}
                  <RecordDeleteButton gateway={deletionGateway} tenantId={tenant.id} entity="order" id={selected.id} noun="Vorgang" label={selected.number} canDelete={permissions.has('orders.delete')} onDone={(outcome) => { setNotice(describeDeleteOutcome(outcome, 'Vorgang', selected.number)); navigate('/vorgaenge'); void load(); }} />
                </>}
              >
                <FlowTrail steps={buildOrderFlow(selected)} ariaLabel={`Statusverlauf ${selected.number}`} />
                {flowTransitions.length > 0 && (
                  <div className="orders-flow-actions">
                    <span className="orders-flow-actions__label">Weiter zu</span>
                    {flowTransitions.map((transition) => (
                      <button
                        key={transition.toStatus}
                        type="button"
                        className={`orders-flow-actions__btn${transition.tone === 'danger' ? ' is-danger' : ''}`}
                        onClick={() => void advanceStatus(selected.id, transition.toStatus)}
                      >
                        {transition.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="forq-record-fields">
                  <RecordField label="Status" value={statusLabels[selected.status]} />
                  <RecordField label="Positionen" value={selected.positions.length} />
                  <RecordField label="Zahlungsziel" value={`${selected.conditions.paymentTermDays} Tage`} />
                  <RecordField label="Brutto" value={money(selected.grossTotal)} />
                </div>
              </RecordPanel>
              {detailChildren}
            </div>
          </div>
        </div>
      )}

      {editor && (
        <div className="orders-editor" role="dialog" aria-modal="true" aria-label="Vorgang erstellen oder bearbeiten">
          <div className="orders-editor__panel">
            <header className="orders-editor__header">
              <div>
                <span className="orders-page__eyebrow">{editor.orderId ? 'Vorgang bearbeiten' : 'Vorgang erstellen'}</span>
                <h2>{editor.step < 5 ? `Schritt ${editor.step} von 4` : 'Vorgangs-Workbench'}</h2>
              </div>
              <button type="button" aria-label="Schließen" onClick={() => setEditor(null)}><X size={20} /></button>
            </header>

            {editor.step < 5 && (
              <div className="orders-wizard__progress">
                {['Vorgangsprofil', 'Kunde', 'Maschine', 'Adressen & Konditionen'].map((label, index) => (
                  <span className={editor.step >= index + 1 ? 'is-active' : ''} key={label}><i>{editor.step > index + 1 ? <Check size={13} /> : index + 1}</i>{label}</span>
                ))}
              </div>
            )}

            {editor.step === 1 && (
              <section className="orders-wizard">
                <h3>Welche Art von Vorgang möchtest du erstellen?</h3>
                <div className="orders-profile-grid">
                  {creatableProfiles.map((value) => (
                    <button type="button" key={value} className={editor.profile === value ? 'is-active' : ''} onClick={() => selectProfile(value)}>
                      <ClipboardList size={18} /><strong>{profileLabels[value]}</strong>
                    </button>
                  ))}
                </div>
                <label>Titel<input autoFocus value={editor.title} onChange={(event) => updateEditor('title', event.target.value)} placeholder={profileLabels[editor.profile]} /></label>
              </section>
            )}

            {editor.step === 2 && (
              <section className="orders-wizard">
                <h3>Kunde und Ansprechpartner</h3>
                <label>Kunde<SearchSelect value={editor.customerId ?? ''} onChange={(next) => selectCustomer(next)} options={[
                  { value: '', label: 'Ohne Kundenbezug' },
                  ...customers.map((customer) => ({ value: customer.id, label: `${customer.number} · ${customer.displayName}` })),
                ]} /></label>
                {onCreateCustomer && !newCustomer ? (
                  <button type="button" className="orders-newcustomer__toggle" onClick={() => { setNewCustomer(emptyNewCustomer()); setCustomerError(null); }}>
                    <UserPlus size={14} /> Neuen Kunden anlegen
                  </button>
                ) : null}
                {newCustomer ? (
                  <div className="orders-newcustomer">
                    <div className="orders-newcustomer__head">
                      <strong>Neuer Kunde</strong>
                      <div className="orders-newcustomer__kind">
                        <label><input type="radio" name="newcustomer-kind" checked={newCustomer.kind === 'company'} onChange={() => setNewCustomer({ ...newCustomer, kind: 'company' })} /> Firma</label>
                        <label><input type="radio" name="newcustomer-kind" checked={newCustomer.kind === 'person'} onChange={() => setNewCustomer({ ...newCustomer, kind: 'person' })} /> Person</label>
                      </div>
                    </div>
                    <div className="orders-form-grid">
                      {newCustomer.kind === 'company' ? (
                        <label>Firmenname<input autoFocus value={newCustomer.companyName} onChange={(event) => setNewCustomer({ ...newCustomer, companyName: event.target.value })} /></label>
                      ) : (
                        <>
                          <label>Vorname<input autoFocus value={newCustomer.firstName} onChange={(event) => setNewCustomer({ ...newCustomer, firstName: event.target.value })} /></label>
                          <label>Nachname<input value={newCustomer.lastName} onChange={(event) => setNewCustomer({ ...newCustomer, lastName: event.target.value })} /></label>
                        </>
                      )}
                      <label>Straße<input value={newCustomer.street} onChange={(event) => setNewCustomer({ ...newCustomer, street: event.target.value })} /></label>
                      <label>PLZ<input value={newCustomer.postalCode} onChange={(event) => setNewCustomer({ ...newCustomer, postalCode: event.target.value })} /></label>
                      <label>Ort<input value={newCustomer.city} onChange={(event) => setNewCustomer({ ...newCustomer, city: event.target.value })} /></label>
                      <label>Telefon<input value={newCustomer.phone} onChange={(event) => setNewCustomer({ ...newCustomer, phone: event.target.value })} /></label>
                      <label>E-Mail<input type="email" value={newCustomer.email} onChange={(event) => setNewCustomer({ ...newCustomer, email: event.target.value })} /></label>
                    </div>
                    {customerError ? <div className="orders-newcustomer__error">{customerError}</div> : null}
                    <div className="orders-newcustomer__actions">
                      <button type="button" className="orders-newcustomer__save" disabled={savingCustomer || !(newCustomer.kind === 'company' ? newCustomer.companyName.trim() : (newCustomer.firstName.trim() || newCustomer.lastName.trim()))} onClick={() => void submitNewCustomer()}>
                        {savingCustomer ? 'Wird angelegt …' : 'Anlegen & übernehmen'}
                      </button>
                      <button type="button" className="orders-newcustomer__cancel" onClick={() => { setNewCustomer(null); setCustomerError(null); }}>Abbrechen</button>
                    </div>
                  </div>
                ) : null}
                <div className="orders-form-grid">
                  <label>Ansprechpartner<input value={editor.contactName} onChange={(event) => updateEditor('contactName', event.target.value)} /></label>
                  <label>E-Mail<input type="email" value={editor.contactEmail} onChange={(event) => updateEditor('contactEmail', event.target.value)} /></label>
                  <label>Telefon<input value={editor.contactPhone} onChange={(event) => updateEditor('contactPhone', event.target.value)} /></label>
                </div>
              </section>
            )}

            {editor.step === 3 && (
              <section className="orders-wizard">
                <h3>Bezugsmaschine – optional</h3>
                <p>{isInternalCustomer
                  ? 'Interner Vorgang: der eigene Verkaufs- und Mietbestand wird angezeigt.'
                  : editor.customerId && !includeAllMachines
                    ? 'Es werden die Maschinen des gewählten Kunden angezeigt.'
                    : 'Es werden alle Maschinen im Bestand angezeigt.'} Bei einem Maschinenverkauf werden weitere Maschinen später als Positionen hinzugefügt.</p>
                <label>Maschine<SearchSelect value={editor.machineId ?? ''} onChange={(next) => { updateEditor('machineId', next || null); setMachineQuery(''); }} onSearch={setMachineQuery} options={[
                  { value: '', label: 'Keine Bezugsmaschine' },
                  ...machines.map((machine) => ({
                    value: machine.id,
                    label: `${machine.number} · ${machine.manufacturer} ${machine.model} · ${machine.serialNumber}`,
                  })),
                ]} /></label>
                {editor.customerId && !isInternalCustomer ? (
                  <label className="orders-inline-check">
                    <input type="checkbox" checked={includeAllMachines} onChange={(event) => setIncludeAllMachines(event.target.checked)} />
                    Auch Maschinen anderer Kunden / aus dem Bestand anzeigen
                  </label>
                ) : null}
              </section>
            )}

            {editor.step === 4 && (
              <section className="orders-wizard orders-wizard--wide">
                <h3>Kunde, Adressen und Grundkonditionen</h3>
                <div className="orders-step4-customer">
                  <label>Kunde suchen / ändern<SearchSelect value={editor.customerId ?? ''} onChange={(next) => selectCustomer(next)} options={[{ value: '', label: 'Ohne Kundenbezug' }, ...customers.map((customer) => ({ value: customer.id, label: `${customer.number} · ${customer.displayName}` }))]} /></label>
                  <p className="orders-step4-customer__info">{editor.customerId ? [editor.contactName, editor.contactEmail, editor.contactPhone].filter(Boolean).join(' · ') || 'Kein Ansprechpartner hinterlegt' : 'Kein Kunde ausgewählt — Adressen können frei eingegeben werden.'}</p>
                </div>
                <div className="orders-address-grid">
                  {editor.addresses.map((address, index) => (
                    <fieldset key={`${address.kind}-${index}`}>
                      <legend>{address.kind === 'billing' ? 'Rechnungsadresse' : address.kind === 'delivery' ? 'Lieferadresse' : 'Einsatzadresse'}{address.kind === 'delivery' && editor.deliveryFollowsBilling ? <span className="orders-address-linked">folgt Rechnungsadresse</span> : null}</legend>
                      <input value={address.companyName ?? ''} onChange={(event) => updateAddress(index, 'companyName', event.target.value)} placeholder="Firma / Name" />
                      <input value={address.street ?? ''} onChange={(event) => updateAddress(index, 'street', event.target.value)} placeholder="Straße" />
                      <div><input value={address.postalCode ?? ''} onChange={(event) => updateAddress(index, 'postalCode', event.target.value)} placeholder="PLZ" /><input value={address.city ?? ''} onChange={(event) => updateAddress(index, 'city', event.target.value)} placeholder="Ort" /></div>
                    </fieldset>
                  ))}
                </div>
                <div className="orders-form-grid orders-form-grid--conditions">
                  <label>Bestellnummer Kunde<input value={String(editor.conditions.customerReference ?? '')} onChange={(event) => updateCondition('customerReference', event.target.value)} /></label>
                  <label>Zahlungsziel<input type="number" min="0" value={Number(editor.conditions.paymentTermDays ?? 14)} onChange={(event) => updateCondition('paymentTermDays', Number(event.target.value))} /><span>Tage</span></label>
                  <label>Skonto<input type="number" min="0" max="100" step="0.1" value={Number(editor.conditions.cashDiscountPercent ?? 0)} onChange={(event) => updateCondition('cashDiscountPercent', Number(event.target.value))} /><span>%</span></label>
                  <label>Skontofrist<input type="number" min="0" value={Number(editor.conditions.cashDiscountDays ?? 0)} onChange={(event) => updateCondition('cashDiscountDays', Number(event.target.value))} /><span>Tage</span></label>
                  <label>Auftragsrabatt<input type="number" min="0" max="100" step="0.1" value={Number(editor.conditions.orderDiscountPercent ?? 0)} onChange={(event) => updateCondition('orderDiscountPercent', Number(event.target.value))} /><span>%</span></label>
                  <label>Wunschtermin<input type="date" value={editor.conditions.requestedDate ?? ''} onChange={(event) => updateCondition('requestedDate', event.target.value || null)} /></label>
                  <label>Belegdatum<input type="date" value={editor.conditions.documentDate ?? ''} onChange={(event) => updateCondition('documentDate', event.target.value || null)} /></label>
                  <label>Belegsprache<select value={editor.conditions.language ?? ''} onChange={(event) => updateCondition('language', event.target.value)}><option value="">Standard (Deutsch)</option><option value="de">Deutsch</option><option value="en">Englisch</option></select></label>
                  {costCenters.length > 0 ? (
                    <label><span className="orders-req-label">Kostenstelle<span className="orders-req"> *</span></span><select value={editor.conditions.costCenterId ?? ''} onChange={(event) => {
                      const entry = costCenters.find((item) => item.id === event.target.value);
                      setEditor((current) => current ? { ...current, conditions: { ...current.conditions, costCenterId: event.target.value || null, costCenterName: entry?.name ?? '' } } : current);
                    }}>
                      <option value="">— bitte wählen —</option>
                      {costCenters.map((item) => <option key={item.id} value={item.id}>{item.code ? `${item.code} · ${item.name}` : item.name}</option>)}
                    </select></label>
                  ) : null}
                  <label className="is-wide">Lieferbedingungen (Incoterms)<DeliveryTermsField value={String(editor.conditions.deliveryTerms ?? '')} onChange={(next) => updateCondition('deliveryTerms', next)} /></label>
                </div>
              </section>
            )}

            {editor.step === 5 && (
              <form className="orders-workbench" onSubmit={(event: FormEvent) => { event.preventDefault(); void saveEditor(); }}>
                <section className="orders-workbench__head">
                  <div><span>Vorgang</span><strong>{editor.orderId ? selected?.number ?? 'bestehend' : 'Neue Nummer nach Speichern'}</strong></div>
                  <label>Art<SearchSelect value={editor.profile} onChange={(next) => selectProfile(next as OrderProfile)} options={[...creatableProfiles, ...(creatableProfiles.includes(editor.profile) ? [] : [editor.profile])].map((value) => ({ value, label: profileLabels[value] }))} /></label>
                  <label>Titel<input value={editor.title} onChange={(event) => updateEditor('title', event.target.value)} /></label>
                  <label>Priorität<SearchSelect value={editor.priority} onChange={(next) => updateEditor('priority', next as WorkbenchDraft['priority'])} options={[{ value: 'low', label: 'Niedrig' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'Hoch' }, { value: 'urgent', label: 'Dringend' }]} /></label>
                  <label>Fällig<input type="date" value={editor.dueAt ?? ''} onChange={(event) => updateEditor('dueAt', event.target.value || null)} /></label>
                </section>

                <details className="orders-workbench__details">
                  <summary>Kunde, Bezugsmaschine, Adressen und Konditionen</summary>
                  <div className="orders-workbench__details-grid">
                    <label>Kunde<SearchSelect value={editor.customerId ?? ''} onChange={(next) => selectCustomer(next)} options={[{ value: '', label: 'Ohne Kundenbezug' }, ...customers.map((customer) => ({ value: customer.id, label: `${customer.number} · ${customer.displayName}` }))]} /></label>
                    <label>Bezugsmaschine<SearchSelect value={editor.machineId ?? ''} onChange={(next) => updateEditor('machineId', next || null)} options={[{ value: '', label: 'Keine' }, ...machines.map((machine) => ({ value: machine.id, label: `${machine.number} · ${machine.manufacturer} ${machine.model}` }))]} /></label>
                    <label>Bestellnummer<input value={String(editor.conditions.customerReference ?? '')} onChange={(event) => updateCondition('customerReference', event.target.value)} /></label>
                    <label>Zahlungsziel<input type="number" value={Number(editor.conditions.paymentTermDays ?? 14)} onChange={(event) => updateCondition('paymentTermDays', Number(event.target.value))} /></label>
                    <label>Skonto %<input type="number" step="0.1" value={Number(editor.conditions.cashDiscountPercent ?? 0)} onChange={(event) => updateCondition('cashDiscountPercent', Number(event.target.value))} /></label>
                    <label>Skontotage<input type="number" value={Number(editor.conditions.cashDiscountDays ?? 0)} onChange={(event) => updateCondition('cashDiscountDays', Number(event.target.value))} /></label>
                    <label>Auftragsrabatt %<input type="number" step="0.1" value={Number(editor.conditions.orderDiscountPercent ?? 0)} onChange={(event) => updateCondition('orderDiscountPercent', Number(event.target.value))} /></label>
                    <label>Lieferbedingungen<DeliveryTermsField value={String(editor.conditions.deliveryTerms ?? '')} onChange={(next) => updateCondition('deliveryTerms', next)} /></label>
                    <label>Bearbeiter<input value={editor.assignedTo} onChange={(event) => updateEditor('assignedTo', event.target.value)} /></label>
                  </div>
                </details>

                <section className="orders-position-editor">
                  <div className="orders-position-editor__hint">Enter oder Tab springt zum nächsten Feld. Im letzten Feld wird automatisch eine neue Position erzeugt. <kbd>Ctrl</kbd> + <kbd>Enter</kbd> speichert.</div>
                  <datalist id="orders-item-options">{items.map((item) => <option value={item.number} key={item.id}>{item.name}</option>)}</datalist>
                  <div className="orders-position-grid orders-position-grid--head"><span>Typ</span><span>Nummer</span><span>Bezeichnung</span><span>Menge</span><span>Einheit</span><span>Einzelpreis</span><span>Rabatt %</span><span>USt. %</span><span>Gesamt</span></div>
                  {editor.positions.map((position, rowIndex) => {
                    let rowTotal = 0;
                    try { rowTotal = calculateOrderPosition(position, rowIndex + 1, position.rowId).grossAmount; } catch { rowTotal = 0; }
                    return (
                      <div className="orders-position-grid" key={position.rowId}>
                        <select data-order-cell={`${rowIndex}-0`} value={position.kind} onChange={(event) => updatePosition(rowIndex, 'kind', event.target.value)} onKeyDown={(event) => handleGridKey(event, rowIndex, 0)}>{Object.entries(positionKindLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
                        <div className="orders-cell-search">
                          <input data-order-cell={`${rowIndex}-1`} list="orders-item-options" value={position.sourceNumber ?? ''} onChange={(event) => updatePosition(rowIndex, 'sourceNumber', event.target.value)} onBlur={(event) => applyCatalogItem(rowIndex, event.target.value)} onKeyDown={(event) => handleGridKey(event, rowIndex, 1)} placeholder="Nr." />
                          <button type="button" className="orders-cell-search__btn" title={position.kind === 'machine' ? 'Maschine suchen' : 'Artikel/Dienstleistung suchen'} onClick={() => setPositionSearch({ rowIndex, mode: position.kind === 'machine' ? 'machine' : 'item' })}>{position.kind === 'machine' ? <Forklift size={13} /> : <Search size={13} />}</button>
                        </div>
                        <div className="orders-cell-search">
                          <input data-order-cell={`${rowIndex}-2`} value={position.description} onChange={(event) => updatePosition(rowIndex, 'description', event.target.value)} onKeyDown={(event) => handleGridKey(event, rowIndex, 2)} />
                          <button type="button" className={`orders-cell-search__btn ${position.customerText ? 'is-on' : ''}`} title="Positionstext bearbeiten (Fett/Kursiv/Unterstreichen/Größe)" onClick={() => setTextEditor(rowIndex)}><Type size={13} /></button>
                        </div>
                        <input data-order-cell={`${rowIndex}-3`} type="number" step="0.001" value={position.quantity ?? 1} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updatePosition(rowIndex, 'quantity', event.target.value)} onKeyDown={(event) => handleGridKey(event, rowIndex, 3)} />
                        <input data-order-cell={`${rowIndex}-4`} value={position.unit ?? ''} onChange={(event) => updatePosition(rowIndex, 'unit', event.target.value)} onKeyDown={(event) => handleGridKey(event, rowIndex, 4)} />
                        <input data-order-cell={`${rowIndex}-5`} type="number" step="0.01" value={position.unitPrice ?? 0} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updatePosition(rowIndex, 'unitPrice', event.target.value)} onKeyDown={(event) => handleGridKey(event, rowIndex, 5)} />
                        <input data-order-cell={`${rowIndex}-6`} type="number" step="0.1" value={position.discountPercent ?? 0} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updatePosition(rowIndex, 'discountPercent', event.target.value)} onKeyDown={(event) => handleGridKey(event, rowIndex, 6)} />
                        {/* Fixed rates, not a free number: a 12 % position cannot be booked and would block the whole invoice. */}
                        <select data-order-cell={`${rowIndex}-7`} value={position.vatRate ?? 19} onChange={(event) => updatePosition(rowIndex, 'vatRate', event.target.value)} onKeyDown={(event) => handleGridKey(event, rowIndex, 7)}>{germanVatRates.map((rate) => <option key={rate} value={rate}>{rate} %</option>)}</select>
                        <strong>{money(rowTotal)}</strong>
                      </div>
                    );
                  })}
                </section>

                <section className="orders-workbench__footer">
                  <label>Interne Notizen<textarea value={editor.notes} onChange={(event) => updateEditor('notes', event.target.value)} /></label>
                  <div className="orders-workbench__totals">
                    <span>Zwischensumme <strong>{money(calculated?.subtotal ?? 0)}</strong></span>
                    <span>Positionsrabatte <strong>− {money(calculated?.positionDiscountTotal ?? 0)}</strong></span>
                    <span>Auftragsrabatt <strong>− {money(calculated?.orderDiscountTotal ?? 0)}</strong></span>
                    <span>Netto <strong>{money(calculated?.netTotal ?? 0)}</strong></span>
                    <span>USt. <strong>{money(calculated?.vatTotal ?? 0)}</strong></span>
                    <span className="is-total">Brutto <strong>{money(calculated?.grossTotal ?? 0)}</strong></span>
                  </div>
                </section>
              </form>
            )}

            {error && <div className="orders-editor__error" role="alert">{error}</div>}
            <footer className="orders-editor__footer">
              {editor.step > 1 && editor.step < 5 && <button type="button" className="is-secondary" onClick={() => updateEditor('step', (editor.step - 1) as WorkbenchDraft['step'])}><ArrowLeft size={16} /> Zurück</button>}
              {/* Abkürzung: Kunde/Maschine/Adressen sind optional -- direkt zu den Positionen (dort wird gespeichert). */}
              {editor.step < 4 && <button type="button" className="is-skip" onClick={() => updateEditor('step', 5)}>Direkt zu den Positionen <ChevronRight size={16} /></button>}
              {editor.step < 4 && <button type="button" onClick={() => updateEditor('step', (editor.step + 1) as WorkbenchDraft['step'])}>Weiter <ChevronRight size={16} /></button>}
              {editor.step === 4 && <button type="button" disabled={costCenters.length > 0 && !editor.conditions.costCenterId} title={costCenters.length > 0 && !editor.conditions.costCenterId ? 'Bitte zuerst eine Kostenstelle wählen (Pflichtfeld)' : undefined} onClick={() => updateEditor('step', 5)}>Workbench öffnen <ChevronRight size={16} /></button>}
              {editor.step === 5 && <button type="button" className="is-secondary" onClick={() => updateEditor('step', 4)}><ArrowLeft size={16} /> Kopfdaten</button>}
              {editor.step === 5 && <button type="button" disabled={saving} onClick={() => void saveEditor()}><Save size={16} /> {saving ? 'Speichert …' : 'Vorgang speichern'}</button>}
            </footer>
            {positionSearch ? (
              <CatalogPickerModal
                mode={positionSearch.mode}
                gateway={gateway}
                tenantId={tenant.id}
                onClose={() => setPositionSearch(null)}
                onPick={(picked) => {
                  if (positionSearch.mode === 'machine') applyMachineOption(positionSearch.rowIndex, picked as OrderMachineOption);
                  else applyItemOption(positionSearch.rowIndex, picked as OrderItemOption);
                  setPositionSearch(null);
                }}
              />
            ) : null}
            {textEditor !== null ? (
              <PositionTextModal
                initialHtml={editor.positions[textEditor]?.customerText ?? ''}
                positionLabel={editor.positions[textEditor]?.description || `Position ${textEditor + 1}`}
                onSave={(html) => { applyPositionText(textEditor, html); setTextEditor(null); }}
                onClose={() => setTextEditor(null)}
              />
            ) : null}
          </div>
        </div>
      )}
      {orderContextMenu}
      {orderDeleteDialog}
    </WorkspacePage>
  );
}

function CatalogPickerModal({ mode, gateway, tenantId, onPick, onClose }: {
  readonly mode: 'item' | 'machine';
  readonly gateway: OrderGateway;
  readonly tenantId: string;
  readonly onPick: (picked: OrderItemOption | OrderMachineOption) => void;
  readonly onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [itemResults, setItemResults] = useState<readonly OrderItemOption[]>([]);
  const [machineResults, setMachineResults] = useState<readonly OrderMachineOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = window.setTimeout(() => {
      const request = mode === 'item'
        ? gateway.searchItemOptions(tenantId, query).then((result) => { if (active) setItemResults(result); })
        : gateway.searchMachineOptions(tenantId, query).then((result) => { if (active) setMachineResults(result); });
      void request.catch(() => {}).finally(() => { if (active) setLoading(false); });
    }, 180);
    return () => { active = false; window.clearTimeout(timer); };
  }, [query, mode, gateway, tenantId]);

  return (
    <div className="orders-picker" role="dialog" aria-modal="true" aria-label={mode === 'item' ? 'Artikel suchen' : 'Maschine suchen'} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="orders-picker__panel">
        <header>
          <div><span>{mode === 'item' ? 'Artikel / Dienstleistung' : 'Maschine'} suchen</span><small>Nummer oder Bezeichnung eingeben, Treffer anklicken.</small></div>
          <button type="button" className="is-secondary" onClick={onClose}><X size={16} /></button>
        </header>
        <label className="orders-picker__search"><Search size={16} /><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder={mode === 'item' ? 'z. B. 10101 oder Hydraulikfilter …' : 'z. B. 10001 oder Toyota …'} /></label>
        <div className="orders-picker__list">
          {loading ? <p className="orders-picker__hint">Suche läuft …</p> : null}
          {!loading && mode === 'item' && itemResults.length === 0 ? <p className="orders-picker__hint">Keine Treffer.</p> : null}
          {!loading && mode === 'machine' && machineResults.length === 0 ? <p className="orders-picker__hint">Keine Treffer.</p> : null}
          {mode === 'item' ? itemResults.map((item) => (
            <button type="button" key={item.id} onClick={() => onPick(item)}>
              <strong>{item.number}</strong>
              <span>{item.name}</span>
              <em>{item.kind === 'article' ? 'Artikel' : 'Dienstleistung'} · {money(item.salesPrice)}</em>
            </button>
          )) : machineResults.map((machine) => (
            <button type="button" key={machine.id} onClick={() => onPick(machine)}>
              <strong>{machine.number}</strong>
              <span>{machine.manufacturer} {machine.model}</span>
              <em>{machine.serialNumber ? `SN ${machine.serialNumber}` : machine.status}</em>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const RICH_ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'SPAN', 'DIV', 'P']);

/** Rich-Text auf eine sichere Teilmenge reduzieren: nur Formatierungs-Tags, nur font-size/-weight/
 *  text-decoration als span-Style, keine Skripte/Handler. Läuft im Browser (DOM-basiert). */
function sanitizeRichHtml(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html;
  const clean = (source: Node): Node[] => {
    const result: Node[] = [];
    source.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) { result.push(node.cloneNode()); return; }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const element = node as Element;
      const children = clean(element);
      if (RICH_ALLOWED_TAGS.has(element.tagName)) {
        const fresh = document.createElement(element.tagName.toLowerCase());
        if (element.tagName === 'SPAN') {
          const safe = ((element.getAttribute('style') ?? '').match(/(font-size|font-weight|text-decoration)\s*:\s*[^;]+/gi) ?? []).join('; ');
          if (safe) fresh.setAttribute('style', safe);
        }
        children.forEach((child) => fresh.appendChild(child));
        result.push(fresh);
      } else {
        children.forEach((child) => result.push(child)); // unerlaubtes Tag entfernen, Inhalt behalten
      }
    });
    return result;
  };
  const container = document.createElement('div');
  clean(template.content).forEach((node) => container.appendChild(node));
  return container.innerHTML.trim();
}

function PositionTextModal({ initialHtml, positionLabel, onSave, onClose }: {
  readonly initialHtml: string;
  readonly positionLabel: string;
  readonly onSave: (html: string) => void;
  readonly onClose: () => void;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editorRef.current) { editorRef.current.innerHTML = initialHtml; editorRef.current.focus(); }
  }, [initialHtml]);

  const format = (command: 'bold' | 'italic' | 'underline') => {
    editorRef.current?.focus();
    document.execCommand(command);
  };
  const applyFontSize = (size: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    const span = document.createElement('span');
    span.style.fontSize = size;
    try { range.surroundContents(span); }
    catch { span.appendChild(range.extractContents()); range.insertNode(span); }
    selection.removeAllRanges();
  };

  return (
    <div className="orders-picker orders-textedit" role="dialog" aria-modal="true" aria-label="Positionstext" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="orders-picker__panel">
        <header>
          <div><span>Positionstext</span><small>{positionLabel}</small></div>
          <button type="button" onClick={onClose}><X size={16} /></button>
        </header>
        <div className="orders-textedit__toolbar">
          <button type="button" title="Fett" onMouseDown={(event) => event.preventDefault()} onClick={() => format('bold')}><Bold size={14} /></button>
          <button type="button" title="Kursiv" onMouseDown={(event) => event.preventDefault()} onClick={() => format('italic')}><Italic size={14} /></button>
          <button type="button" title="Unterstrichen" onMouseDown={(event) => event.preventDefault()} onClick={() => format('underline')}><Underline size={14} /></button>
          <span className="orders-textedit__sep" />
          <button type="button" title="Kleine Schrift" onMouseDown={(event) => event.preventDefault()} onClick={() => applyFontSize('10px')}>A−</button>
          <button type="button" title="Normale Schrift" onMouseDown={(event) => event.preventDefault()} onClick={() => applyFontSize('13px')}>A</button>
          <button type="button" title="Große Schrift" onMouseDown={(event) => event.preventDefault()} onClick={() => applyFontSize('17px')}>A+</button>
        </div>
        <div ref={editorRef} className="orders-textedit__area" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="Positionstext-Eingabe" />
        <div className="orders-textedit__actions">
          <button type="button" className="is-secondary" onClick={onClose}>Abbrechen</button>
          <button type="button" onClick={() => onSave(sanitizeRichHtml(editorRef.current?.innerHTML ?? ''))}>Übernehmen</button>
        </div>
      </div>
    </div>
  );
}
